/**
 * @module useHeightCache
 * @description This module provides a hook for caching item heights using an LRU (Least Recently Used) cache.
 * It is designed to be used in virtual scrolling components to optimize performance by storing and retrieving the heights of rendered items.
 *
 * @description このモジュールは、LRU (Least Recently Used) キャッシュを使用してアイテムの高さをキャッシュするためのフックを提供します。
 * 仮想スクロールコンポーネントで使用されることを想定しており、レンダリングされたアイテムの高さを保存および取得することでパフォーマンスを最適化します。
 */
import { useLruCache } from "./useLruCache.ts"

// LRU キャッシュに保存する高さの測定値の最大数
const HEIGHT_CACHE_CAPACITY = 10000

/**
 * A custom hook that provides a cache for storing the heights of items, backed by an LRU cache.
 * This helps in optimizing virtual scrolling by avoiding re-computation of item heights.
 * The key is the item's index (number), and the value is its height (number).
 *
 * LRU キャッシュを利用して、アイテムの高さを保存するためのキャッシュを提供するカスタムフック。
 * これにより、アイテムの高さの再計算を回避し、仮想スクロールを最適化します。
 * キーはアイテムのインデックス (number)、値はその高さ (number) です。
 */
export const useHeightCache = () => {
    // useLruCache フックを初期化し、指定された容量でキャッシュインスタンスを作成
    const { get, set, has, clear } = useLruCache<number, number>(HEIGHT_CACHE_CAPACITY)

    // キャッシュを操作するための関数 (get, set, has, clear) を返す
    return { get, set, has, clear }
}
